import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Col, Form, Modal, Row } from 'react-bootstrap';
import { Card, CardBody, CardTitle } from '@spark/ui-components/components/Card';
import { Button, ButtonWithLoading } from '@spark/ui-components/components/Button';
import Icon from '@spark/ui-components/components/Icon';
import SearchSelect from '@spark/ui-components/components/form/SearchSelect';
import { notification } from '@spark/ui-components/utils/notification';
import { sendDepotsEmailClientes, syncDepotsAnexo } from '@spark/services';
import { getClientes, getClienteEmailsByClienteId } from '@spark/services/clientes';
import { Link } from 'react-router-dom';
import * as AnexoStyles from '../../../../Cadastro/components/Forms/index-styled';

const getClienteIdFromDepot = (depot) =>
  depot?.ClienteId ??
  depot?.IdCliente ??
  depot?.clienteId ??
  depot?.idCliente ??
  '';

const formatCnpj = (value) => {
  const digits = String(value || '').replace(/\D/g, '');
  if (digits.length !== 14) return digits;
  return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12)}`;
};

const EncerramentoTab = ({ depotId, depot }) => {
  const [clienteSelecionado, setClienteSelecionado] = useState(null);
  const [assunto, setAssunto] = useState('');
  const [anexos, setAnexos] = useState([]);
  const [anexosModal, setAnexosModal] = useState([]);
  const [anexoModalOpen, setAnexoModalOpen] = useState(false);
  const [enviandoEmail, setEnviandoEmail] = useState(false);
  const [emailsCliente, setEmailsCliente] = useState([]);
  const [emailsClienteLoading, setEmailsClienteLoading] = useState(false);

  useEffect(() => {
    const initialClienteId = getClienteIdFromDepot(depot);
    if (initialClienteId !== null && initialClienteId !== undefined && initialClienteId !== '') {
      setClienteSelecionado({
        value: String(initialClienteId),
        label: String(initialClienteId),
      });
    } else {
      setClienteSelecionado(null);
    }
  }, [depot]);

  useEffect(() => {
    const numeroContainer = String(depot?.NumeroContainer || '').trim();
    if (!numeroContainer) return;
    setAssunto((prev) => (prev && prev.trim() ? prev : numeroContainer));
  }, [depot?.NumeroContainer]);

  const anexosSelecionadosTexto = useMemo(() => {
    if (!anexos.length) return 'Nenhum arquivo selecionado';
    if (anexos.length === 1) return '1 arquivo selecionado';
    return `${anexos.length} arquivos selecionados`;
  }, [anexos]);

  const anexosPreview = useMemo(
    () =>
      anexos.map((file, index) => ({
        index,
        id: `${file?.name || 'arquivo'}-${index}`,
        name: file?.name || `Arquivo ${index + 1}`,
        url: URL.createObjectURL(file),
      })),
    [anexos],
  );

  useEffect(
    () => () => {
      anexosPreview.forEach((item) => {
        URL.revokeObjectURL(item.url);
      });
    },
    [anexosPreview],
  );

  const handleOpenAnexoModal = useCallback(() => {
    setAnexosModal([]);
    setAnexoModalOpen(true);
  }, []);

  const handleCloseAnexoModal = useCallback(() => {
    if (enviandoEmail) return;
    setAnexoModalOpen(false);
    setAnexosModal([]);
  }, [enviandoEmail]);

  const handleFileChangeModal = useCallback((event) => {
    setAnexosModal(Array.from(event.target.files || []));
  }, []);

  const handleAdicionarAnexosModal = useCallback(() => {
    if (!anexosModal.length) {
      notification.warning('Selecione ao menos um arquivo.');
      return;
    }

    const novosArquivos = anexosModal;
    setAnexos((prev) => {
      const mapa = new Map(
        prev.map((arquivo) => [`${arquivo.name}-${arquivo.size}-${arquivo.lastModified}`, arquivo]),
      );
      novosArquivos.forEach((arquivo) => {
        mapa.set(`${arquivo.name}-${arquivo.size}-${arquivo.lastModified}`, arquivo);
      });
      return Array.from(mapa.values());
    });

    setAnexosModal([]);
    setAnexoModalOpen(false);
  }, [anexosModal]);

  const handleRemoverAnexoSelecionado = useCallback((indexToRemove) => {
    setAnexos((prev) => prev.filter((_, index) => index !== indexToRemove));
  }, []);

  useEffect(() => {
    let mounted = true;

    const loadEmailsCliente = async () => {
      const clienteIdNumero = Number(clienteSelecionado?.value);
      if (!Number.isInteger(clienteIdNumero) || clienteIdNumero <= 0) {
        setEmailsCliente([]);
        return;
      }

      try {
        setEmailsClienteLoading(true);
        const response = await getClienteEmailsByClienteId(clienteIdNumero);
        if (!mounted) return;

        const lista = Array.isArray(response)
          ? response
          : response?.registros || response?.data || [];

        const normalizados = lista
          .map((item) => ({
            id: item?.id ?? item?.Id ?? null,
            email: String(item?.email ?? item?.Email ?? '').trim(),
          }))
          .filter((item) => item.email);

        setEmailsCliente(normalizados);
      } catch (error) {
        if (!mounted) return;
        console.error('Erro ao carregar emails do cliente:', error);
        setEmailsCliente([]);
        const message =
          error?.response?.data?.message ||
          error?.response?.data?.mensagem ||
          error?.message ||
          'Nao foi possivel carregar emails do cliente.';
        notification.error(message);
      } finally {
        if (mounted) {
          setEmailsClienteLoading(false);
        }
      }
    };

    loadEmailsCliente();

    return () => {
      mounted = false;
    };
  }, [clienteSelecionado?.value]);

  const handleSearchClientes = useCallback(async (searchTerm) => {
    const term = String(searchTerm || '').trim();
    if (!term) return [];

    try {
      const digits = term.replace(/\D/g, '');
      const searchParams = {
        pagina: 1,
        quantidadePorPagina: 30,
      };

      if (digits.length > 0) {
        searchParams.Cnpj = digits;
      } else if (term.length >= 2) {
        searchParams.Nome = term;
      } else {
        return [];
      }

      const response = await getClientes({
        ...searchParams,
      });
      const registros = Array.isArray(response)
        ? response
        : response?.registros || response?.data || [];

      return registros.map((cliente) => {
        const id = cliente?.Id ?? cliente?.id;
        const nome = cliente?.Nome ?? cliente?.nome ?? '';
        const cnpjRaw = cliente?.CNPJ ?? cliente?.Cnpj ?? cliente?.cnpj ?? '';
        const cnpjFormatado = formatCnpj(cnpjRaw);
        const label = nome && cnpjFormatado ? `${nome} - ${cnpjFormatado}` : nome || cnpjFormatado || String(id || '');
        return { value: String(id || ''), label };
      }).filter((option) => option.value);
    } catch (error) {
      console.error('Erro ao buscar clientes:', error);
      return [];
    }
  }, []);

  const handleEnviarEmail = useCallback(
    async (event) => {
      event.preventDefault();

      const depotIdNumero = Number(depotId || depot?.Id);
      if (!Number.isInteger(depotIdNumero) || depotIdNumero <= 0) {
        notification.warning('DepotId invalido para envio.');
        return;
      }

      const clienteIdNumero = Number(clienteSelecionado?.value);
      if (!Number.isInteger(clienteIdNumero) || clienteIdNumero <= 0) {
        notification.warning('Selecione um cliente valido.');
        return;
      }

      if (!anexos.length) {
        notification.warning('Adicione ao menos um anexo.');
        return;
      }

      try {
        setEnviandoEmail(true);

        // 1) Persiste os anexos via sync-anexo por container/cpfMotorista
        const grupos = new Map();
        anexos.forEach((arquivo) => {
          const nomeOriginal = arquivo?.name || '';
          const baseName = nomeOriginal.replace(/\.[^/.]+$/, '');
          const parts = baseName.split('_');
          const nomeMotorista = parts[1] || '';
          const cpfMotorista = String(parts[2] || '').replace(/\D/g, '');
          const container = parts.length > 3 ? parts.slice(3).join('_') : '';

          if (!nomeMotorista || !cpfMotorista || !container) return;

          const key = `${container}||${cpfMotorista}`;
          if (!grupos.has(key)) {
            grupos.set(key, { container, cpfMotorista, arquivos: [] });
          }
          grupos.get(key).arquivos.push(arquivo);
        });

        for (const group of grupos.values()) {
          await syncDepotsAnexo({
            container: group.container,
            cpfMotorista: group.cpfMotorista,
            arquivos: group.arquivos,
            tag: 'RIC',
          });
        }

        // 2) Dispara o email para o cliente com os anexos selecionados
        await sendDepotsEmailClientes({
          depotId: depotIdNumero,
          clienteId: clienteIdNumero,
          assunto: assunto?.trim() || '',
          anexos,
        });
        notification.success('Email enviado com sucesso.');
        setAssunto(String(depot?.NumeroContainer || '').trim());
        setAnexos([]);
      } catch (error) {
        console.error('Erro ao enviar email para cliente:', error);
        const message =
          error?.response?.data?.message ||
          error?.response?.data?.mensagem ||
          error?.message ||
          'Nao foi possivel enviar o email.';
        notification.error(message);
      } finally {
        setEnviandoEmail(false);
      }
    },
    [anexos, assunto, clienteSelecionado, depotId, depot?.Id, depot?.NumeroContainer]
  );

  return (
    <>
      <Row className="mb-3">
        <Col md={12}>
          <Card>
            <CardBody>
              <CardTitle>Enviar Email para Cliente</CardTitle>
              <Form onSubmit={handleEnviarEmail}>
                <Row>
                  <Col md={12}>
                    <SearchSelect
                      name="encerramentoClienteId"
                      label="Cliente (CNPJ) *"
                      required={false}
                      value={clienteSelecionado}
                      onChange={setClienteSelecionado}
                      placeholder="Buscar cliente por CNPJ"
                      onSearch={handleSearchClientes}
                      minSearchLength={1}
                      maxResults={30}
                      filterOption={() => true}
                      disabled={enviandoEmail}
                    />

                    <div style={{ marginTop: 10 }}>
                      <strong style={{ display: 'block', marginBottom: 8 }}>Emails vinculados ao cliente</strong>
                      {!clienteSelecionado?.value ? (
                        <p className="mb-0 text-muted">Selecione um cliente para visualizar os emails vinculados.</p>
                      ) : emailsClienteLoading ? (
                        <p className="mb-0 text-muted">Carregando emails...</p>
                      ) : !emailsCliente.length ? (
                        <p className="mb-0 text-muted">
                          Nenhum email vinculado para este cliente.{' '}
                          <Link to={`/pages/clientes/detalhes/${clienteSelecionado?.value}?tab=emails`}>
                            Deseja cadastrar um email?
                          </Link>
                        </p>
                      ) : (
                        <div className="d-flex flex-wrap" style={{ gap: 8 }}>
                          {emailsCliente.map((item) => (
                            <span
                              key={`${item.id ?? 'email'}-${item.email}`}
                              className="badge"
                              style={{
                                fontSize: 13,
                                fontWeight: 500,
                                background: '#edf3ff',
                                color: '#1f4da3',
                                border: '1px solid #d9e6ff',
                                padding: '8px 10px',
                              }}
                            >
                              {item.email}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </Col>
                </Row>
                <Row className="mt-4">
                  <Col md={12}>
                    <Form.Group controlId="encerramentoAssunto">
                      <Form.Label>Assunto</Form.Label>
                      <Form.Control
                        type="text"
                        value={assunto}
                        onChange={(event) => setAssunto(event.target.value)}
                        placeholder="Assunto do email"
                        disabled={enviandoEmail}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={12} className="mt-4">
                    <Form.Group controlId="encerramentoAnexos">
                      <Form.Label>Anexos *</Form.Label>
                      <AnexoStyles.AnexosGrid style={{ marginTop: 8 }}>
                        <AnexoStyles.AddTagButton
                          type="button"
                          onClick={handleOpenAnexoModal}
                          aria-label="Adicionar anexos"
                          disabled={enviandoEmail}
                        >
                          <Icon name="plus" size={16} />
                        </AnexoStyles.AddTagButton>

                        {anexosPreview.map((item) => (
                          <AnexoStyles.AnexoItem key={item.id}>
                            <AnexoStyles.Caption>
                              <AnexoStyles.FileTag
                                type="button"
                                onClick={() => window.open(item.url, '_blank', 'noopener')}
                                title={item.name}
                              >
                                {item.name}
                              </AnexoStyles.FileTag>
                              <AnexoStyles.RemoveButton
                                type="button"
                                onClick={() => handleRemoverAnexoSelecionado(item.index)}
                                aria-label={`Remover ${item.name}`}
                              >
                                <Icon name="trash" size={14} />
                              </AnexoStyles.RemoveButton>
                            </AnexoStyles.Caption>
                          </AnexoStyles.AnexoItem>
                        ))}
                      </AnexoStyles.AnexosGrid>
                      <small className="text-muted">{anexosSelecionadosTexto}</small>
                    </Form.Group>
                  </Col>
                </Row>
                <div className="d-flex justify-content-end mt-3">
                  <ButtonWithLoading
                    variant="primary"
                    type="submit"
                    loading={enviandoEmail ? 'true' : undefined}
                  >
                    Enviar
                  </ButtonWithLoading>
                </div>
              </Form>
            </CardBody>
          </Card>
        </Col>
      </Row>
      <Modal show={anexoModalOpen} onHide={handleCloseAnexoModal} centered backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>Anexar arquivos</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group controlId="encerramentoAnexosModal">
            <Form.Label>Arquivo(s)*</Form.Label>
            <Form.Control
              type="file"
              multiple
              onChange={handleFileChangeModal}
              disabled={enviandoEmail}
            />
          </Form.Group>
          <small className="text-muted">
            {!anexosModal.length
              ? 'Nenhum arquivo selecionado'
              : anexosModal.length === 1
                ? '1 arquivo selecionado'
                : `${anexosModal.length} arquivos selecionados`}
          </small>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" type="button" onClick={handleCloseAnexoModal} disabled={enviandoEmail}>
            Cancelar
          </Button>
          <Button variant="primary" type="button" onClick={handleAdicionarAnexosModal} disabled={enviandoEmail}>
            Adicionar
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default EncerramentoTab;
